import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../services/supabase';
import { api } from '../services/api';

// Types pour l'authentification
export interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<{ user: User | null; error: AuthError | null }>;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<{ user: User | null; error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string, captchaToken?: string) => Promise<{ error: AuthError | null }>;
  updateProfile: (updates: any) => Promise<{ user: User | null; error: AuthError | null }>;
  checkUserProfile: () => Promise<{ hasProfile: boolean; profile: any | null; error: any | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook pour utiliser le contexte d'authentification
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Provider d'authentification
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    
    // 🔥 GESTION GLOBALE DU CODE PKCE - Au démarrage de l'app
    const handlePKCECode = async () => {
      const url = new URL(window.location.href);
      const code = url.searchParams.get('code');
      const error = url.searchParams.get('error');

      
      // 🚨 DÉTECTER ERREURS DANS LE HASH (lien expiré/consommé par scanner)
      const hash = window.location.hash;
      if (hash.includes('error=')) {
        
        // Parser le hash
        const hashParams = new URLSearchParams(hash.substring(1));
        const errorCode = hashParams.get('error_code');
        
        // Nettoyer le hash pour éviter de polluer l'URL
        window.history.replaceState({}, '', url.pathname);
        
        if (errorCode === 'otp_expired') {
          // NE PAS rediriger automatiquement
          // L'utilisateur verra un message ou ira sur les pages manuellement
        }
        
        setLoading(false);
        return;
      }
      
      // Gérer les erreurs dans les query params
      if (error) {
        url.search = '';
        window.history.replaceState({}, '', url.pathname);
        setLoading(false);
        return;
      }

      // Si on a un code PKCE, l'échanger UNE SEULE FOIS
      if (code) {
        
        try {
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          
          if (exchangeError) {
          } else {
            setSession(data.session);
            setUser(data.session?.user ?? null);
            await new Promise(resolve => setTimeout(resolve, 800));
          }
        } catch (err) {
        }
        
        // Nettoyer l'URL
        url.search = '';
        window.history.replaceState({}, '', url.pathname);
      }

    // Récupérer la session actuelle
      const { data: { session } } = await supabase.auth.getSession();
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    };
    
    // Exécuter la gestion PKCE
    handlePKCECode();

    // Écouter les changements d'authentification
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Logs détaillés selon l'événement
      switch (event) {
        case 'SIGNED_IN':
          
          // Vérifier si l'utilisateur doit créer un profil tuteur  
          const pendingTutorProfile = localStorage.getItem('pendingTutorProfile');
          if (pendingTutorProfile === 'true') {
            localStorage.removeItem('pendingTutorProfile');
            
            // Vérifier si l'utilisateur a déjà un profil
            setTimeout(async () => {
              try {
                if (!session?.user?.id) {
                  window.location.href = '/login';
                  return;
                }
                
                const { data: profileData, error: profileError } = await api.profiles.getByUserId(session.user.id);
                
                if (profileError || !profileData) {
                  // Pas de profil trouvé, rediriger vers la création
                  window.location.href = '/create-profile';
                } else {
                  // Profil existant, rediriger vers le profil
                  window.location.href = '/profile';
                }
              } catch (err) {
                // En cas d'erreur, rediriger vers la création par sécurité
                window.location.href = '/create-profile';
              }
            }, 1000);
          }
          break;
        case 'SIGNED_OUT':
          // Nettoyer le localStorage lors de la déconnexion
          localStorage.removeItem('pendingTutorProfile');
          break;
        case 'TOKEN_REFRESHED':
          break;
        case 'USER_UPDATED':
          break;
        case 'PASSWORD_RECOVERY':
          // Rediriger vers la page de reset si on est sur une autre page
          if (window.location.pathname !== '/reset-password') {
            setTimeout(() => {
              window.location.href = '/reset-password';
            }, 500);
          }
          break;
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Inscription
  const signUp = async (email: string, password: string, metadata?: any) => {
    try {
      setLoading(true);

      // Extraire le captchaToken des metadata
      const { captchaToken, ...userMetadata } = metadata || {};

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userMetadata,
          captchaToken, // Token hCaptcha pour la vérification serveur
          emailRedirectTo: 'https://www.tutormatch-heg-ge.ch/create-profile', // Redirection après confirmation email
        }
      });

      if (error) {
        return { user: null, error };
      }

      return { user: data.user, error: null };
    } catch (err) {
      return { user: null, error: err as AuthError };
    } finally {
      setLoading(false);
    }
  };

  // Connexion
  const signIn = async (email: string, password: string, captchaToken?: string) => {
    try {
      setLoading(true);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: {
          captchaToken, // Token hCaptcha pour la vérification serveur (optionnel)
        }
      });

      if (error) {
        return { user: null, error };
      }

      return { user: data.user, error: null };
    } catch (err) {
      return { user: null, error: err as AuthError };
    } finally {
      setLoading(false);
    }
  };

  // Déconnexion
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        return { error };
      }

      return { error: null };
    } catch (err) {
      return { error: err as AuthError };
    }
  };

  // Réinitialisation du mot de passe
  const resetPassword = async (email: string, captchaToken?: string) => {
    try {
      // ⚠️ IMPORTANT: Utiliser www. dans l'URL pour correspondre à la config Supabase
      const redirectUrl = `https://www.tutormatch-heg-ge.ch/reset-password`;
      
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
        captchaToken, // Token hCaptcha pour la vérification serveur
      });

      if (error) {
        return { error };
      }

      return { error: null };
    } catch (err) {
      return { error: err as AuthError };
    }
  };

  // Mise à jour du profil
  const updateProfile = async (updates: any) => {
    try {
      
      const { data, error } = await supabase.auth.updateUser(updates);

      if (error) {
        return { user: null, error };
      }

      return { user: data.user, error: null };
    } catch (err) {
      return { user: null, error: err as AuthError };
    }
  };

  // Vérifier l'existence d'un profil tuteur
  const checkUserProfile = async () => {
    try {
      if (!user) {
        return { hasProfile: false, profile: null, error: 'Utilisateur non connecté' };
      }

      
      const { data, error } = await api.profiles.getByUserId(user.id);

      if (error) {
        return { hasProfile: false, profile: null, error };
      }

      const hasProfile = !!data;
      
      return { hasProfile, profile: data, error: null };
    } catch (err) {
      return { hasProfile: false, profile: null, error: err };
    }
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateProfile,
    checkUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 